import { Route, Routes, Navigate, Outlet } from 'react-router'
import { Layout, LogIn, SignUp } from 'src/pages'
import { ChatDemo } from 'src/pages/chat-demo'
import { ExpensesMainContentView } from 'src/pages/expenses'
import { PageNotFound } from 'src/pages/page-not-found'
import { NoAccessPage } from 'src/pages/no-access'
import { ReceiptsPage } from 'src/pages/receipts-page'
import { ReportsPage } from 'src/pages/reports-page'
import { RequestPasswordReset } from 'src/pages/request-password-reset'
import { ResetPassword } from 'src/pages/reset-password'
import { useAuth } from 'src/stores/auth'
import { getOAuthAuthorizeNext } from 'src/utils/auth'
import { usePermissions } from 'src/hooks/use-permissions'
import { Reservations } from 'src/pages/reservations-list'
import { ReservationDetailPage } from 'src/pages/reservation-detail'
import ColorPalettePreview from 'src/components/color-palette-preview'
import { ExpenseCreateFormWrapper } from 'src/components/expense-create-form'
import ComponentPreview from 'src/pages/component-preview'
import { ReservationGuestFormWrapper } from 'src/pages/reservation-guest-form'
import { SubLayout } from 'src/pages/sub-layout'
import { Chat } from 'src/pages/chat'
import { Integrations } from 'src/pages/integrations'
import { GoogleOAuthCallback } from 'src/components/google-oauth-callback'
import { ReportDetailPageWrapper } from 'src/components/reports/report-detail-page'
import { OrganizationSettings } from 'src/pages/organization-settings'
import { VendorsPage } from 'src/pages/vendors-page'
import { ExportTemplatesPage } from 'src/pages/export-templates-page'
import { ExportTemplateDetailPage } from 'src/pages/export-template-detail-page'
import { Properties } from 'src/pages/properties'
import { PropertyDetailView } from 'src/pages/property-detail'
import { StatementReviewWrapper } from 'src/pages/statement-review'
import { StatementUploadWrapper } from 'src/pages/statement-upload'
import { StatementsPage } from 'src/pages/statements-page'
import { NotificationsPage } from 'src/pages/notifications-page'
import { NotificationPreferences } from 'src/pages/notification-preferences'
import { PaymentAccountsPage } from 'src/pages/payment-accounts-page'
import { GuestSurveyPage } from 'src/pages/guest-survey'
import { LegalPage } from 'src/pages/legal-page'
import { ReportingGuidePage } from 'src/pages/reporting-guide-page'

/**
 * Module routes with their required permission codenames.
 * The order here determines the priority when computing the first accessible route.
 */
export const MODULE_ROUTES: { path: string; permission: string }[] = [
  { path: '/reservations', permission: 'reservations.access' },
  { path: '/expenses', permission: 'expenses.access' },
  { path: '/expense-reports', permission: 'reports.access' },
  { path: '/receipts', permission: 'receipts.access' },
  { path: '/statements', permission: 'statements.access' },
  { path: '/vendors', permission: 'vendors.access' },
  { path: '/export-templates', permission: 'export_templates.access' },
  { path: '/chat-clients', permission: 'chat.access' },
]

/**
 * Returns the path of the first module the current user is allowed to access,
 * or null if no modules are accessible.
 */
export const useFirstAccessibleRoute = (): string | null => {
  const { hasPermissionInAnyOrg } = usePermissions()
  const first = MODULE_ROUTES.find(({ permission }) => hasPermissionInAnyOrg(permission))
  return first?.path ?? null
}

/**
 * Wraps a route group that requires a specific permission.
 * If the user lacks the permission they are redirected to the first
 * accessible route (or to /no-access).
 */
const ProtectedRoute = ({ permission }: { permission: string }) => {
  const { hasPermission } = usePermissions()
  const firstAccessible = useFirstAccessibleRoute()

  if (!hasPermission(permission)) {
    return <Navigate to={firstAccessible ?? '/no-access'} replace />
  }

  return <Outlet />
}

const PrivateRoutes = () => {
  return (
    <>
      <Route element={<ProtectedRoute permission="reservations.access" />}>
        <Route path={`/reservations`}>
          <Route index element={<Reservations />} />
          <Route element={<SubLayout />}>
            <Route path="reservation" element={<ReservationDetailPage />} />
            <Route path=":id" element={<ReservationDetailPage />} />
            {/* Reservation travels as a `?reservation=<id>` query param — the
                unified mechanism shared with the guest self-fill link. The
                legacy `:id/guests` path segment below keeps working. */}
            <Route path="guests" element={<ReservationGuestFormWrapper />} />
            <Route path=":id/guests" element={<ReservationGuestFormWrapper />} />
            <Route path=":id/guests/:formId" element={<ReservationGuestFormWrapper />} />
            <Route
              path=":id/guests/guest/:guestId/:formId"
              element={<ReservationGuestFormWrapper />}
            />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute permission="expenses.access" />}>
        <Route path={`/expenses`}>
          <Route index element={<ExpensesMainContentView />} />
          <Route element={<SubLayout />}>
            <Route path="expense" element={<ExpenseCreateFormWrapper />} />
            <Route path="expense/:id" element={<ExpenseCreateFormWrapper />} />
            <Route path="statements/new" element={<StatementUploadWrapper />} />
            <Route path="statements/:id/review" element={<StatementReviewWrapper />} />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute permission="reports.access" />}>
        <Route path={`/expense-reports`}>
          <Route index element={<ReportsPage />} />
          <Route element={<SubLayout />}>
            <Route path="report" element={<ReportDetailPageWrapper />} />
            <Route path="report/:id" element={<ReportDetailPageWrapper />} />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute permission="receipts.access" />}>
        <Route path={`/receipts`} element={<ReceiptsPage />} />
      </Route>

      <Route element={<ProtectedRoute permission="expenses.access" />}>
        <Route path={`/payment-accounts`} element={<PaymentAccountsPage />} />
      </Route>

      <Route element={<ProtectedRoute permission="statements.access" />}>
        <Route path={`/statements`} element={<StatementsPage />} />
      </Route>

      <Route element={<ProtectedRoute permission="vendors.access" />}>
        <Route path={`/vendors`} element={<VendorsPage />} />
      </Route>

      <Route element={<ProtectedRoute permission="export_templates.access" />}>
        <Route path={`/export-templates`}>
          <Route index element={<ExportTemplatesPage />} />
          <Route element={<SubLayout />}>
            <Route path="template" element={<ExportTemplateDetailPage />} />
            <Route path="template/:id" element={<ExportTemplateDetailPage />} />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute permission="chat.access" />}>
        <Route path={`/chat-clients`}>
          <Route index element={<Chat />} />
          <Route path=":conversationId" element={<Chat />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute permission="properties.access" />}>
        <Route path={`/properties`}>
          <Route index element={<Properties />} />
          <Route element={<SubLayout />}>
            <Route path=":id" element={<PropertyDetailView />} />
          </Route>
        </Route>
      </Route>

      {/* Utility / settings routes — no module permission required */}
      {/* Billing now lives inside Settings; keep the old path working as a redirect. */}
      <Route path={`/billing`} element={<Navigate to="/organization-settings#billing" replace />} />
      <Route path={`/chat-demo`} element={<ChatDemo />} />
      <Route path={`/integrations`} element={<Integrations />} />
      <Route path={`/notifications`} element={<NotificationsPage />} />
      <Route path={`/notification-preferences`} element={<NotificationPreferences />} />
      <Route path={`/organization-settings`} element={<OrganizationSettings />} />
      {/* Legacy routes — WhatsApp numbers and access tokens now live under Settings. */}
      <Route
        path={`/whatsapp-numbers`}
        element={<Navigate to="/organization-settings" replace />}
      />
      {/* Templates moved into the /chat-clients Templates tab. */}
      <Route
        path={`/whatsapp-templates`}
        element={<Navigate to="/chat-clients?tab=templates" replace />}
      />
      <Route
        path={`/user-settings`}
        element={<Navigate to="/organization-settings?tab=account" replace />}
      />
      <Route path={`/oauth/google/callback`} element={<GoogleOAuthCallback />} />
      <Route path={`/colors`} element={<ColorPalettePreview />} />
      <Route path={`/component-preview`} element={<ComponentPreview />} />
      <Route path={`/help/reporting-setup`} element={<ReportingGuidePage />} />
      <Route path={`/no-access`} element={<NoAccessPage />} />
    </>
  )
}

const AuthRedirect = () => {
  const firstAccessible = useFirstAccessibleRoute()
  return <Navigate to={firstAccessible ?? '/no-access'} replace />
}

export const AppRoutes = () => {
  const token = useAuth.use.token()
  const isAuth = Boolean(token)

  return (
    <Routes>
      {/* Guest self-fill survey — unauthenticated, token-scoped. Rendered
          outside ProtectedRoute and the auth split so a guest with only a link
          (no org account) can reach exactly one reservation's survey. */}
      <Route path="/guest-survey/:token" element={<GuestSurveyPage />} />
      <Route path="/guest-survey/:token/:instanceId" element={<GuestSurveyPage />} />

      {/* Legal documents — public, for logged-in and logged-out visitors alike. */}
      <Route path="/legal/:doc" element={<LegalPage />} />

      {/* Root redirect */}
      <Route path="/" element={isAuth ? <AuthRedirect /> : <Navigate to="/log-in" />} />

      {/* Auth routes - redirect to first accessible module if already authenticated */}
      <Route
        path="/log-in"
        element={isAuth && !getOAuthAuthorizeNext() ? <AuthRedirect /> : <LogIn />}
      />
      <Route path="/sign-up" element={isAuth ? <AuthRedirect /> : <SignUp />} />
      <Route path="/request-reset" element={isAuth ? <AuthRedirect /> : <RequestPasswordReset />} />
      <Route
        path="/password/reset/confirm/:userId/:token"
        element={isAuth ? <AuthRedirect /> : <ResetPassword />}
      />

      {/* Protected routes */}
      {isAuth ? (
        <Route path="/" element={<Layout />}>
          {PrivateRoutes()}
          <Route path="*" element={<PageNotFound />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/log-in" />} />
      )}
    </Routes>
  )
}
